'use client';

import { AppImage, Icon, Typography } from '@workspace/ui/common';

import { processHeading } from '@workspace/lib/heading-utils';
import { cn } from '@workspace/lib';
import type { TextIconListBlock } from './text-icon-list.types';
import Link from 'next/link';
import { getBackgroundClass, shouldOpenInNewTab } from '@workspace/lib';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { resolveIconUrl } from './resolveIconUrl';

export function TextIconList({ block }: { block: TextIconListBlock }) {
  if (!block || typeof block !== 'object') return null;
  const {
    heading,
    description,
    textIconItemList,
    switchIconTextList = false,
    backgroundColor,
  } = block || ({} as TextIconListBlock);
  const { text: headingText } = processHeading(heading);
  const { text: subheadingText } = processHeading(textIconItemList?.subheading);
  const items = Array.isArray(textIconItemList?.textIconItems) ? textIconItemList!.textIconItems : [];
  const backgroundClass = getBackgroundClass(backgroundColor);

  return (
    <div className={cn('w-full', backgroundClass)}>
      <div className="porta-container">
        <section className="py-xl px-md md:p-4xl">
          <div
            className={cn(
              'flex flex-col md:flex-row gap-lg md:gap-3xl items-start',
              switchIconTextList && 'md:flex-row-reverse',
            )}
          >
            {/* Left: Heading + Description */}
            <div className="md:shrink-0 w-full md:w-147.5">
              <div className="space-y-md md:w-full">
                <div className="flex w-full flex-col gap-lg">
                  {headingText ? (
                    <Typography variant="h3" className={cn('font-bold  md:mr-xl !text-2xl md:!text-4xl ')}>
                      {headingText}
                    </Typography>
                  ) : null}

                  {Array.isArray(description) && description.length ? (
                    <div className="max-w-none mr-1.5 md:mr-0">
                      <CustomPortableText value={description || []} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Right: Subheading + Icon/Text List */}
            <div className="md:flex-1 w-full flex flex-col md:gap-xl gap-lg md:self-center">
              {subheadingText ? (
                <Typography variant="h4" className={cn('font-bold !text-2xl md:!text-heading-large ')}>
                  {subheadingText}
                </Typography>
              ) : null}

              <div className="flex flex-col md:gap-md gap-sm" role="list">
                {items.slice(0, 12).map((item, idx) => {
                  const src = resolveIconUrl(item.icon);
                  const href = (item.title?.link?.href ?? '').trim();
                  const hasLink = !!href;
                  const openNew = shouldOpenInNewTab(item.title?.link);
                  const alt = item?.icon?.alt;

                  return (
                    <div key={item?._key ?? `texticon-${idx}`} className="flex gap-sm" role="listitem">
                      {/* ICON */}
                      <div className="shrink-0 w-12.5 h-11.25 flex items-center justify-center">
                        {src ? (
                          <AppImage
                            src={src}
                            alt={alt || 'Icon'}
                            width={50}
                            height={45}
                            loading="lazy"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Icon name={'IconHome'} className="w-full h-full" />
                        )}
                      </div>

                      {/* TEXT */}
                      <div className="space-y-1">
                        {item.title?.text &&
                          (hasLink ? (
                            <Link
                              href={href}
                              target={openNew ? '_blank' : '_self'}
                              rel={openNew ? 'noopener noreferrer' : undefined}
                              className="inline-block"
                            >
                              <Typography
                                variant="link"
                                className={cn(
                                  '!text-lg !font-sora hover:text-primary-500',
                                  item.title?.isBold ? 'font-bold' : '!font-normal',
                                )}
                              >
                                {item.title?.text}
                              </Typography>
                            </Link>
                          ) : (
                            <Typography
                              variant="h5"
                              className={cn('!text-lg  text-black', item.title?.isBold ? 'font-bold' : '!font-normal')}
                            >
                              {item.title?.text}
                            </Typography>
                          ))}

                        {Array.isArray(item.description) && item.description.length ? (
                          <div className="prose prose-gray max-w-none">
                            <CustomPortableText value={item.description || []} />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
